import React, { FC, useState } from 'react';
import { Button } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import Overlay from '../../pge-overlay/_Overlay';
import { usePathContext } from '../../../providers/PathProvider';
import useLink from '../../../hooks/useLink';
import { isOverlay } from '../../../util/type-checking';
import { FileType, LinkType } from '../../../constants';
import { ImageLinkProps as BaseProps } from './types.d';

interface Props extends BaseProps {
  imageOnlyHover?: boolean;
  showContentAsBlock?: boolean;
  hideLabel?: boolean;
}

const useStyles = makeStyles<
  Theme,
  { imageOnlyHover?: boolean; showContentAsBlock?: boolean }
>((theme: Theme) =>
  createStyles({
    root: ({ imageOnlyHover, showContentAsBlock }) => ({
      padding: theme.spacing(0),
      width: '100%',
      height: '100%',
      minWidth: imageOnlyHover ? 'unset' : undefined,
      ...(imageOnlyHover
        ? {
            display: 'inline-block',
            '&:hover, &:focus': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
            '& .imageLink-content': {
              display: showContentAsBlock ? 'block' : 'inline-block',
            },
            '&:hover .imageLink-content': {
              opacity: 0.8,
            },
          }
        : {
            width: '100%',
            '&:hover, &:focus': {
              opacity: 0.8,
            },
          }),
      '& > .MuiButton-label': {
        ...(showContentAsBlock && {
          display: 'block',
        }),
      },
    }),
    content: {
      width: '100%',
      display: 'inline-block',
      lineHeight: 0,
    },
    label: {
      width: 1,
      height: 1,
      position: 'absolute',
      overflow: 'hidden',
    },
  }),
);

const ImageLink: FC<Props> = ({
  children,
  link,
  text,
  imageOnlyHover,
  showContentAsBlock,
  hideLabel,
}) => {
  if (!children) {
    return null;
  }

  const href: string = useLink(link);
  const overlay = isOverlay(link) ? link : null;
  const isButton = link === undefined || !!overlay;
  const isAnchor = !isButton && !!href;

  if (!href) {
    return <>{children}</>;
  }

  const externalLink: boolean = LinkType.HTTP.test(href);
  const documentLink: boolean = FileType.DOC.test(href);
  const imageLink: boolean = FileType.IMG.test(href);
  const isBlank: boolean = externalLink || documentLink || imageLink;
  const { resolve } = usePathContext();
  const classes = useStyles({ imageOnlyHover, showContentAsBlock });

  const [open, setOpen] = useState(false);

  return (
    <>
      <Button
        disableRipple={imageOnlyHover}
        disableFocusRipple={imageOnlyHover}
        disableElevation={imageOnlyHover}
        className={`imageLink ${classes.root}`}
        component={isAnchor ? 'a' : 'button'}
        href={isAnchor ? resolve(href) : undefined}
        target={isBlank ? '_blank' : undefined}
        rel={isBlank ? 'noopener noreferrer' : undefined}
        onClick={overlay ? () => setOpen(true) : undefined}
      >
        <span className={`imageLink-content ${classes.content}`}>
          {children}
        </span>
        {!hideLabel && (
          <span className={classes.label}>{text || 'Learn More'}</span>
        )}
      </Button>
      {overlay && (
        <Overlay
          content={overlay.content}
          open={open}
          setOpen={setOpen}
          entryName={overlay.entryName}
          statusAlert={overlay.statusAlert}
        />
      )}
    </>
  );
};

export default ImageLink;
